import React from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { CalendarDays, GraduationCap, Bus, HeartPulse, MapPin, Clock, Users } from "lucide-react";
import { Layout } from "../components/Layout";
import { SponsorSlot } from "../components/SponsorSlot";
import * as a from "../api";

const TABS = [
  { key: "EVENT", label: "Eventi", icon: CalendarDays, enabled: true },
  { key: "COURSE", label: "Corsi", icon: GraduationCap, enabled: true },
  { key: "TRANSPORT", label: "Trasporti", icon: Bus, enabled: true },
  { key: "SERVICE", label: "Servizi", icon: HeartPulse, enabled: true },
];

const eur = (n) => `€${(Number(n) || 0).toFixed(0)}`;
const priceLabel = { per_person: "a persona", per_team: "a squadra", per_group: "a gruppo", per_ticket: "a ingresso", fixed: "" };

function EventCard({ e, index }) {
  const isCourse = e.type === "COURSE";
  const isService = e.type === "SERVICE";
  const isTransport = e.type === "TRANSPORT";
  const cs = e.course_summary || {};
  const ss = e.service_summary || {};
  const ts = e.transport_summary || {};
  const so = isCourse ? cs.sold_out : isService ? false : isTransport ? ts.sold_out : e.availability?.sold_out;
  const badge = isCourse ? "CORSO" : isService ? "SERVIZIO" : isTransport ? "TRASPORTO" : "EVENTO";
  return (
    <Link to={`/eventi/${e.slug}`} data-testid={`event-card-${e.id}`}
      className="group block rounded-2xl overflow-hidden bg-white border border-slate-200 hover:shadow-xl hover:-translate-y-0.5 transition-all animate-float-up"
      style={{ animationDelay: `${Math.min(index * 60, 400)}ms` }}>
      <div className="relative h-44 bg-slate-100 overflow-hidden">
        {e.images?.[0] && <img src={e.images[0]} alt={e.title} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />}
        <span className="absolute top-3 left-3 rounded-full bg-primary/90 text-white text-[11px] font-bold px-3 py-1 tracking-wide">{badge}</span>
        {so && <span className="absolute top-3 right-3 rounded-full bg-red-500 text-white text-[11px] font-bold px-3 py-1">SOLD OUT</span>}
      </div>
      <div className="p-4">
        <h3 className="font-heading font-semibold text-slate-900 line-clamp-1">{e.title}</h3>
        <p className="text-sm text-slate-500 line-clamp-2 mt-1 min-h-[2.5rem]">{e.short_description}</p>
        <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-400">
          {isCourse ? <>
            {e.organizer && <span className="flex items-center gap-1"><GraduationCap className="h-3.5 w-3.5" />{e.organizer}</span>}
            {cs.next_start && <span className="flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" />dal {cs.next_start}</span>}
            {typeof cs.editions_count === "number" && <span>{cs.editions_count} edizioni</span>}
          </> : isService ? <>
            {ss.duration_min > 0 && <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{ss.duration_min} min</span>}
            {ss.location_mode && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{ss.location_mode === "DOMICILIO_CLIENTE" ? "domicilio" : ss.location_mode === "ENTRAMBE" ? "presso/domicilio" : "presso operatore"}</span>}
          </> : isTransport ? <>
            <span className="flex items-center gap-1"><Bus className="h-3.5 w-3.5" />{e.origin} → {e.destination}</span>
            {ts.next_departure && <span className="flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" />{ts.next_departure}</span>}
          </> : <>
            {e.event_date && <span className="flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" />{e.event_date}</span>}
            {e.start_time && <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{e.start_time}</span>}
            {e.location_name && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{e.location_name}</span>}
          </>}
        </div>
        <div className="mt-3 flex items-center justify-between">
          <span className="text-primary font-bold">{eur(e.price)} <span className="text-xs font-normal text-slate-400">{priceLabel[e.pricing_type] || ""}</span></span>
          {isCourse
            ? (!so && typeof cs.total_remaining === "number" && <span className="text-[11px] text-emerald-600 font-semibold flex items-center gap-1"><Users className="h-3 w-3" />{cs.total_remaining} posti</span>)
            : isService
            ? <span className="text-[11px] text-secondary font-semibold">Prenota</span>
            : isTransport
            ? (!so && typeof ts.total_remaining === "number" && ts.total_remaining > 0 ? <span className="text-[11px] text-emerald-600 font-semibold flex items-center gap-1"><Users className="h-3 w-3" />{ts.total_remaining} posti</span> : <span className="text-[11px] text-secondary font-semibold">{e.transport_mode === "ON_REQUEST" ? "Richiedi" : "Prenota"}</span>)
            : (typeof e.availability?.remaining === "number" && !so && <span className="text-[11px] text-emerald-600 font-semibold flex items-center gap-1"><Users className="h-3 w-3" />{e.availability.remaining} posti</span>)}
        </div>
      </div>
    </Link>
  );
}

export default function Eventi() {
  const [params, setParams] = useSearchParams();
  const rawTab = params.get("t") || "EVENT";
  const tab = TABS.some((x) => x.key === rawTab) ? rawTab : "EVENT";
  const active = TABS.find((x) => x.key === tab);
  const setTab = (key) => { const p = new URLSearchParams(params); if (key === "EVENT") p.delete("t"); else p.set("t", key); setParams(p); };

  const { data: items = [], isLoading } = useQuery({
    queryKey: ["publicActivities", tab],
    queryFn: () => a.getActivities({ type: tab }),
    enabled: !!active?.enabled,
  });

  return (
    <Layout>
      <div className="bg-gradient-to-b from-slate-50 to-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-secondary mb-2">Cristalda · Territorio</div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-semibold tracking-tight text-slate-900">Eventi & Servizi</h1>
          <p className="mt-3 text-base text-slate-500 max-w-2xl">Eventi, corsi, trasporti e servizi del territorio del Gargano. Prenota o iscriviti in pochi tocchi.</p>
          <div className="mt-6 flex gap-2 overflow-x-auto no-scrollbar pb-2" data-testid="activity-tabs">
            {TABS.map((x) => {
              const Icon = x.icon;
              const isActive = tab === x.key;
              return (
                <button key={x.key} data-testid={`activity-tab-${x.key}`} onClick={() => setTab(x.key)}
                  className={`shrink-0 flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium transition-colors ${isActive ? "bg-primary text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-primary"}`}>
                  <Icon className="h-4 w-4" /> {x.label}
                  {!x.enabled && <span className="ml-1 text-[9px] font-bold uppercase text-slate-400">soon</span>}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        {!active?.enabled ? (
          <div className="text-center py-24 max-w-lg mx-auto animate-float-up" data-testid="activity-coming-soon">
            <div className="h-20 w-20 rounded-3xl bg-primary/8 text-primary flex items-center justify-center mx-auto mb-6">
              <active.icon className="h-9 w-9" />
            </div>
            <h2 className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight text-slate-900">{active?.label}</h2>
            <p className="mt-3 text-lg font-accent italic text-secondary">Prossimamente.</p>
            <p className="mt-4 text-sm text-slate-500">Stiamo preparando questa sezione. Torna presto.</p>
          </div>
        ) : isLoading ? (
          <div className="text-center py-20 text-slate-400">Caricamento…</div>
        ) : items.length === 0 ? (
          <div className="text-center py-24 max-w-lg mx-auto animate-float-up" data-testid="events-empty-state">
            <div className="h-20 w-20 rounded-3xl bg-primary/8 text-primary flex items-center justify-center mx-auto mb-6"><active.icon className="h-9 w-9" /></div>
            <h2 className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight text-slate-900">{active.label}</h2>
            <p className="mt-3 text-lg font-accent italic text-secondary">{tab === "COURSE" ? "Nessun corso in programma al momento." : tab === "SERVICE" ? "Nessun servizio disponibile al momento." : tab === "TRANSPORT" ? "Nessun trasporto disponibile al momento." : "Nessun evento in programma al momento."}</p>
            <p className="mt-4 text-sm text-slate-500">Gli operatori del Gargano stanno preparando le prossime attività. Torna presto.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="events-grid">
            {items.map((e, i) => <EventCard key={e.id} e={e} index={i} />)}
          </div>
        )}
      </div>
      <SponsorSlot placement="ESPERIENZE" className="pb-14" />
    </Layout>
  );
}
